import { BookingModel } from '@app/core/models';
import { environment } from '@env/environment';

/**
 * Custom attributes describing the guest's active booking, shown to the
 * operator in the inbox sidebar so they know which stay a question is about.
 *
 * Shared by every chat provider — the vendors differ in how they are pushed
 * (Intercom: `updateUser({ customAttributes })`, Crisp:
 * `Crisp.session.setData()`), not in what they contain.
 *
 * All values are coerced to strings: Intercom silently rejects the whole
 * update when a single attribute is `undefined`, which used to leave the
 * previous booking id stale. Crisp only accepts string/number/boolean too.
 */
export type BookingChatAttributes = Record<string, string>;

export interface ActiveLocationLike {
  address?: string;
  floor?: string;
  apartment?: string;
}

export function buildBookingChatAttributes(
  activeBooking: BookingModel,
): BookingChatAttributes {
  const property = activeBooking.property;
  const location = property?.location;
  const propertyManagerUrl =
    property && property._id && property.organization
      ? `${environment.products.propertyManager}/organizations/${property.organization}/properties/${property._id}`
      : '';

  return {
    app_type: 'guests',
    'Booking Id': activeBooking._id ?? '',
    Property: property?.name ?? '',
    'Property address': location?.address ?? '',
    Floor: location?.floor ?? '',
    Unit: location?.apartment ?? '',
    'Check-in date': activeBooking.start ?? '',
    'Check-out date': activeBooking.end ?? '',
    'Stay type': activeBooking.stayType ?? '',
    'Property Manager URL': propertyManagerUrl,
  };
}

export function buildEmptyBookingChatAttributes(
  activeLocation?: ActiveLocationLike | null,
): BookingChatAttributes {
  return {
    app_type: 'guests',
    'Booking Id': '',
    Property: '',
    'Property address': activeLocation?.address ?? '',
    Floor: activeLocation?.floor ?? '',
    Unit: activeLocation?.apartment ?? '',
    'Check-in date': '',
    'Check-out date': '',
    'Stay type': 'on-the-go',
    'Property Manager URL': '',
  };
}
